import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Home as HomeIcon, 
  Building2, 
  Flame, 
  Droplets, 
  Hammer, 
  Wind,
  Phone,
  ArrowRight
} from 'lucide-react';
import ServiceCard from '../components/ServiceCard';
import { Button } from '../components/ui/button';

const ASSETS = {
  residential: "https://images.unsplash.com/photo-1771681278369-e2a5480b8c29?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwyfHxiYXRocm9vbSUyMHNpbmslMjBmYXVjZXQlMjBsdXh1cnklMjBtb2Rlcm4lMjB3aGl0ZXxlbnwwfHx8fDE3NzE5NTY0OTR8MA&ixlib=rb-4.1.0&q=85",
  commercial: "https://images.pexels.com/photos/3627486/pexels-photo-3627486.jpeg?auto=compress&cs=tinysrgb&w=800",
  boilers: "https://images.pexels.com/photos/7937300/pexels-photo-7937300.jpeg?auto=compress&cs=tinysrgb&w=800",
  waterHeaters: "https://images.pexels.com/photos/4092030/pexels-photo-4092030.jpeg?auto=compress&cs=tinysrgb&w=800",
  remodeling: "https://images.pexels.com/photos/15062162/pexels-photo-15062162.jpeg?auto=compress&cs=tinysrgb&w=800",
  hvac: "https://images.pexels.com/photos/32497161/pexels-photo-32497161.jpeg?auto=compress&cs=tinysrgb&w=800",
};

const services = [
  {
    title: 'Residential Plumbing',
    description: 'Complete plumbing solutions for your home. From repairs and maintenance to new installations, our licensed technicians handle all residential plumbing needs.',
    image: ASSETS.residential,
    link: '/services/residential',
    icon: HomeIcon,
  },
  {
    title: 'Commercial Plumbing',
    description: 'Professional plumbing services for businesses, office buildings, and commercial properties. We minimize downtime and ensure code compliance.',
    image: ASSETS.commercial,
    link: '/services/commercial',
    icon: Building2,
  },
  {
    title: 'Boilers',
    description: 'Expert boiler installation, maintenance, and repair services. We service steam and hot water boilers for residential and commercial applications.',
    image: ASSETS.boilers,
    link: '/services/boilers',
    icon: Flame,
  },
  {
    title: 'Water Heaters',
    description: 'Water heater installation, repair, and replacement. We work with tank and tankless systems to ensure you always have hot water.',
    image: ASSETS.waterHeaters,
    link: '/services/water-heaters',
    icon: Droplets,
  },
  {
    title: 'Remodeling',
    description: 'Transform your kitchen and bathroom with our expert remodeling services. We handle all plumbing aspects of your renovation project.',
    image: ASSETS.remodeling,
    link: '/services/remodeling',
    icon: Hammer,
  },
  {
    title: 'Air Conditioning',
    description: 'Stay cool with our comprehensive AC installation, repair, and maintenance services. We keep your home comfortable year-round.',
    image: ASSETS.hvac,
    link: '/services/air-conditioning',
    icon: Wind,
  },
];

const Services = () => {
  return (
    <div data-testid="services-page">
      {/* Hero Section */}
      <section className="py-24 bg-navy" data-testid="services-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="font-manrope text-cyan mb-4">What We Offer</p>
            <h1 className="font-oswald uppercase text-5xl md:text-6xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="font-manrope text-slate-300 text-xl max-w-2xl mx-auto">
              Comprehensive plumbing and heating solutions for homes and businesses throughout the Twin Cities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-testid="services-grid">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <ServiceCard {...service} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services List */}
      <section className="py-20 bg-slate-50" data-testid="additional-services">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="section-title text-4xl mb-4">Complete Service List</h2>
            <p className="font-manrope text-slate-600 max-w-2xl mx-auto">
              In addition to our main services, we offer a wide range of plumbing and heating solutions.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              'Backflow Prevention',
              'Boiler Maintenance',
              'Commercial Kitchens',
              'Faucet Install/Repair',
              'Garbage Disposals',
              'Gas Piping',
              'Industrial Piping',
              'Pipe Thawing',
              'Piping Replacement',
              'Plumbing Fixtures',
              'Pumps',
              'RPZ Testing',
              'Reverse Osmosis',
              'Sewage Ejectors',
              'Sump Pumps',
              'Toilets',
              'Washer/Dryer Hookups',
              'Water Filters',
              'Water Softeners',
              'Air Conditioning',
            ].map((item, index) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.03 }}
                className="flex items-center gap-2 p-3 bg-white rounded-md shadow-sm"
              >
                <ArrowRight className="w-4 h-4 text-orange flex-shrink-0" />
                <span className="font-manrope text-sm text-slate-700">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-navy" data-testid="services-cta">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-oswald uppercase text-4xl md:text-5xl font-bold text-white mb-4">
              Need a Service Not Listed?
            </h2>
            <p className="font-manrope text-slate-300 text-xl mb-8 max-w-2xl mx-auto">
              Contact us to discuss your specific needs. We're here to help!
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="btn-primary text-lg" data-testid="services-contact-btn">
                  Contact Us
                </Button>
              </Link>
              <a href="tel:651-738-9354">
                <button className="bg-white text-[#14366C] hover:bg-gray-100 font-oswald font-bold uppercase tracking-wider px-8 py-4 rounded-md text-lg flex items-center transition-colors" data-testid="services-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  651-738-9354
                </button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
